import { Tooltip } from '@ctw/shared/components/tooltip';
import { useUserPatientSummarizationContext } from '@ctw/shared/content/summarization/user-patient-summarization-context';
import { useFeatureFlag } from '@ctw/shared/context/feature-flag-provider';
import { numberOfDaysBetween } from '@ctw/shared/utils/dates';
import { tw, twx } from '@ctw/shared/utils/tailwind';
import { addDays, endOfMonth } from 'date-fns';
import pluralize from 'pluralize';

const USAGE_RING_VIEW = 16;
const USAGE_RING_STROKE = 2;
const USAGE_RING_RADIUS = (USAGE_RING_VIEW - USAGE_RING_STROKE) / 2;
const USAGE_RING_CENTER = USAGE_RING_VIEW / 2;
const USAGE_RING_CIRCUMFERENCE = 2 * Math.PI * USAGE_RING_RADIUS;

interface PatientSummarizationUsageTickerProps {
  showHoverDetail?: boolean;
}

export const PatientSummarizationUsageTicker = ({
  showHoverDetail = false,
}: PatientSummarizationUsageTickerProps) => {
  const {
    summariesAllotted,
    summariesUsed,
    summarizationLimitApproaching,
    summarizationLimitReached,
  } = useUserPatientSummarizationContext();

  const hasFreeUsageLimit = useFeatureFlag('summarization-free-usage-limit');
  if (!hasFreeUsageLimit) {
    return null;
  }

  const safeAllotted = Math.max(summariesAllotted, 1);
  const fractionUsed = summarizationLimitReached
    ? 1
    : Math.min(1, Math.max(0, summariesUsed / safeAllotted));
  const progressDashOffset = USAGE_RING_CIRCUMFERENCE * (1 - fractionUsed);
  const percentUsed = Math.round(fractionUsed * 100);

  const numberOfDaysUntilReset = numberOfDaysBetween({
    start: new Date(),
    end: addDays(endOfMonth(new Date()), 1),
  });

  const chip = (
    <div
      className={twx('flex items-center gap-0.5 whitespace-nowrap rounded-md border px-2 py-1', {
        'border-border-main/45 bg-background-disabled/35': !(
          summarizationLimitReached || summarizationLimitApproaching
        ),
        'border-caution-text/30 bg-caution-background':
          !summarizationLimitReached && summarizationLimitApproaching,
        'border-error-text/30 bg-error-background': summarizationLimitReached,
      })}
    >
      <svg
        viewBox={`0 0 ${USAGE_RING_VIEW} ${USAGE_RING_VIEW}`}
        width={USAGE_RING_VIEW}
        height={USAGE_RING_VIEW}
        aria-hidden="true"
        role="presentation"
        className={tw`h-4 w-4 shrink-0`}
      >
        <circle
          cx={USAGE_RING_CENTER}
          cy={USAGE_RING_CENTER}
          r={USAGE_RING_RADIUS}
          fill="none"
          className={twx({
            'stroke-content-subtle/30': !(
              summarizationLimitReached || summarizationLimitApproaching
            ),
            'stroke-content-subtle/50': summarizationLimitReached || summarizationLimitApproaching,
          })}
          strokeWidth={USAGE_RING_STROKE}
        />
        <circle
          cx={USAGE_RING_CENTER}
          cy={USAGE_RING_CENTER}
          r={USAGE_RING_RADIUS}
          fill="none"
          className={twx({
            'stroke-content-subtle/55': !(
              summarizationLimitReached || summarizationLimitApproaching
            ),
            'stroke-caution-main/85': !summarizationLimitReached && summarizationLimitApproaching,
            'stroke-error-main/85': summarizationLimitReached,
          })}
          strokeWidth={USAGE_RING_STROKE}
          strokeLinecap="round"
          strokeDasharray={USAGE_RING_CIRCUMFERENCE}
          strokeDashoffset={progressDashOffset}
          transform={`rotate(-90 ${USAGE_RING_CENTER} ${USAGE_RING_CENTER})`}
        />
      </svg>
      <div
        className={twx('pl-1 font-medium text-sm tracking-wide', {
          'text-content-subtle/75': !(summarizationLimitReached || summarizationLimitApproaching),
          'text-caution-text/85': !summarizationLimitReached && summarizationLimitApproaching,
          'text-error-text/85': summarizationLimitReached,
        })}
      >
        {summariesUsed.toLocaleString('en-US')}&#8239;/&#8239;
        {summariesAllotted.toLocaleString('en-US')}
      </div>
    </div>
  );

  if (!showHoverDetail) {
    return chip;
  }

  return (
    <Tooltip
      id="patient-summarization-usage"
      place="bottom-start"
      noArrow={true}
      tooltipClassName={tw`!bg-background-main !rounded-lg !border !border-border-main !shadow-lg !opacity-100`}
      content={
        <div className={tw`min-w-48`}>
          <div className={tw`flex items-center justify-between`}>
            <div className={tw`font-semibold text-content-main text-sm`}>AI Summaries Usage</div>
            <div className={tw`font-medium text-content-subtle text-sm`}>{percentUsed}%</div>
          </div>
          <div className={tw`mt-1.5 text-content-main text-sm`}>
            <b>
              {summariesUsed.toLocaleString('en-US')} of {summariesAllotted.toLocaleString('en-US')}
            </b>{' '}
            free summaries used this month
          </div>
          <div className={tw`mt-0.5 text-content-subtle text-xs`}>
            Resets in {pluralize('day', numberOfDaysUntilReset, true)}
          </div>
        </div>
      }
    >
      {chip}
    </Tooltip>
  );
};
